import React from "react";
import { NavLink } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import ContactCTA from "../components/ContactCTA";
import FaqSection from "../components/FaqSection";
import { PhosphorIcon } from "../components/PhosphorIcon";
import useReveal from "../hooks/useReveal";
import "../styles/decision-page.css";

const ROLES = [
  {
    term: "GSP",
    full: "GST Suvidha Provider",
    region: "India",
    body: "An authorised intermediary that connects your ERP to the GST Network (GSTN) and the e-invoice IRP for IRN generation, returns and e-way bills.",
    icon: "FileText",
  },
  {
    term: "ASP",
    full: "Accredited Service Provider",
    region: "UAE, KSA, Malaysia",
    body: "A government-accredited provider that validates and transmits invoices to the tax authority's platform (EmaraTax, Fatoora, MyInvois) on your behalf.",
    icon: "ShieldCheck",
  },
  {
    term: "PDP",
    full: "Plateforme de Dématérialisation Partenaire",
    region: "France / EU",
    body: "A state-registered platform through which French B2B e-invoices are issued and received, with e-reporting to the DGFiP. Similar access-point roles exist across PEPPOL.",
    icon: "GitMerge",
  },
];

const CRITERIA = [
  { c: "Mandate accreditation", why: "The provider must be authorised for your specific country mandate — accreditation is not transferable across regimes." },
  { c: "ERP integration fit", why: "How cleanly it connects to Oracle, SAP, Zoho or your stack — via API, middleware or connector — decides your real cost." },
  { c: "Coverage / multi-country", why: "If you operate across geographies, a provider (or combination) covering your full footprint reduces vendor sprawl." },
  { c: "Volume & throughput", why: "Clearance/reporting SLAs and rate limits must match your invoice volumes, especially for high-volume B2C." },
  { c: "Validation depth", why: "Pre-submission validation against the authority's schema is what stops rejections and audit exposure." },
  { c: "Archival & audit", why: "Compliant, tamper-evident archival for the legally required retention period in each jurisdiction." },
];

const FAQS = [
  {
    question: "What is the difference between a GSP, ASP and PDP?",
    answer:
      "A GSP (GST Suvidha Provider) is the India-specific intermediary to the GSTN and IRP. An ASP (Accredited Service Provider) is a government-accredited provider that transmits invoices to the tax authority in markets like the UAE, KSA and Malaysia. A PDP (Partner Dematerialization Platform) is the French registered platform for issuing and receiving B2B e-invoices. They play the same essential role — connecting you to the mandate — but the term, accreditation and rules differ by country.",
  },
  {
    question: "How do I choose the right e-invoicing provider?",
    answer:
      "Start with mandate accreditation for your country, then weigh ERP integration fit, multi-country coverage, invoice volume/SLA, validation depth and archival. The cheapest licence is rarely the cheapest project — integration effort usually dominates total cost. Cloudare assesses these against your actual stack and recommends a fit rather than a brand.",
  },
  {
    question: "Can one provider cover UAE, KSA, India and the EU?",
    answer:
      "Some providers cover multiple regimes, but few cover all of them at production depth. Many enterprises use a primary provider plus one or two specialists. Because Cloudare owns the ERP-side integration layer, you can mix providers by geography without rebuilding your core integration.",
  },
  {
    question: "Does Cloudare prefer a particular provider?",
    answer:
      "No. We are vendor-neutral and integrate with Avalara, ecosio, Cygnet, Clear, Complyance, BDO, Taxilla, Pagero and others. We recommend based on your mandate, volumes and ERP — and if you already have a preferred provider, we integrate with it.",
  },
  {
    question: "What if I want to switch providers later?",
    answer:
      "Because Cloudare builds and owns the ERP-to-provider integration layer, switching providers means re-pointing the integration, not rebuilding it. This avoids the lock-in that comes from embedding a single provider's connector deep inside your ERP.",
  },
  {
    question: "Can you help us select a PDP or ASP?",
    answer:
      "Yes. Choosing the right PDP (France/EU) or ASP (UAE, KSA, Malaysia) is one of the most common things we help with. We assess your mandate, invoice volumes, geographies and existing ERP, then recommend an accredited provider that fits — and because we are vendor-neutral, the recommendation is based on your situation, not a commercial tie.",
  },
  {
    question: "We already use a provider but aren't happy — can you help?",
    answer:
      "Yes. We frequently step into existing setups that aren't performing — rejections, failed clearances, brittle integrations or poor support. We review the current implementation, fix the integration and validation issues, and if the provider itself is the problem, we can migrate you to a better-fit accredited provider while keeping your ERP-side integration intact.",
  },
];

export default function DecisionPage() {
  useReveal();
  return (
    <React.Fragment>
      <Seo
        path="/gsp-asp-pdp"
        title="GSP vs ASP vs PDP — How to Choose an E-Invoicing Provider | Cloudare"
        description="GSP, ASP and PDP explained, and how to choose an e-invoicing provider for India GST, UAE, KSA, Malaysia and the EU. Vendor-neutral guidance from Cloudare."
      />
      <Navbar />

      <section className="dp-hero reveal">
        <div className="dp-hero__inner">
          <p className="dp-eyebrow">Decision guide</p>
          <h1 className="dp-hero__title">GSP vs ASP vs PDP: how to choose</h1>
          <p className="dp-hero__subhead">
            Three letters that decide how your invoices reach the tax authority.
            Here is what each means, when it applies, and how to pick the right
            provider for your mandate and ERP.
          </p>
        </div>
      </section>

      {/* Role cards */}
      <section className="dp-section">
        <div className="dp-roles reveal-stagger">
          {ROLES.map((r) => (
            <article className="dp-role" key={r.term}>
              <div className="dp-role__icon">
                <PhosphorIcon name={r.icon} size={24} />
              </div>
              <div className="dp-role__head">
                <span className="dp-role__term">{r.term}</span>
                <span className="dp-role__region">{r.region}</span>
              </div>
              <p className="dp-role__full">{r.full}</p>
              <p className="dp-role__body">{r.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Comparison table */}
      <section className="dp-section dp-section--alt">
        <header className="dp-section__head reveal">
          <p className="dp-eyebrow">At a glance</p>
          <h2>GSP, ASP and PDP compared</h2>
        </header>
        <div className="dp-table-wrap reveal">
          <table className="dp-table">
            <thead>
              <tr>
                <th>Aspect</th>
                <th>GSP (India)</th>
                <th>ASP (UAE / KSA / MY)</th>
                <th>PDP (France / EU)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Authority</td>
                <td>GSTN / IRP</td>
                <td>FTA, ZATCA, LHDN</td>
                <td>DGFiP / PEPPOL</td>
              </tr>
              <tr>
                <td>Core role</td>
                <td>IRN, e-way bill, returns</td>
                <td>Validate &amp; transmit to portal</td>
                <td>Issue / receive &amp; e-report</td>
              </tr>
              <tr>
                <td>Model</td>
                <td>API to GSTN</td>
                <td>Clearance / reporting</td>
                <td>4/5-corner exchange</td>
              </tr>
              <tr>
                <td>Format</td>
                <td>GST INV-01 JSON</td>
                <td>UBL 2.1 / PINT AE</td>
                <td>Factur-X / UBL / CII</td>
              </tr>
              <tr>
                <td>Accreditation</td>
                <td>GSP authorisation</td>
                <td>Government accreditation</td>
                <td>State registration</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Selection criteria */}
      <section className="dp-section">
        <header className="dp-section__head reveal">
          <p className="dp-eyebrow">What to weigh</p>
          <h2>Six criteria for choosing a provider</h2>
        </header>
        <div className="dp-criteria reveal-stagger">
          {CRITERIA.map((x, i) => (
            <div className="dp-criterion" key={i}>
              <span className="dp-criterion__num">{i + 1}</span>
              <div>
                <h3>{x.c}</h3>
                <p>{x.why}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="dp-links reveal">
          <NavLink to="/partners" className="dp-textlink">
            See the providers we integrate →
          </NavLink>
          <NavLink to="/einvoicing-erp-integration" className="dp-textlink">
            E-invoicing integration with your ERP →
          </NavLink>
        </div>
      </section>

      <div id="faq">
        <FaqSection
          eyebrow="Frequently asked questions"
          title="GSP / ASP / PDP — FAQ"
          faqs={FAQS}
          emitJsonLd={true}
        />
      </div>

      <ContactCTA
        title="Let us recommend the right provider"
        subtitle="Vendor-neutral assessment against your mandate, volumes and ERP — then we own the integration."
        primaryLabel="Book a provider assessment"
      />
      <Footer />
    </React.Fragment>
  );
}
